/**
 * 🎙️ VOICE ROOM PROVIDER - Abstraction Layer
 * 
 * Architecture modulaire pour les salons vocaux
 * Actuellement : LiveKit
 * Peut être remplacé par : Daily.co, Agora, Twilio, etc.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// CONTEXT
// ============================================

const VoiceRoomContext = createContext(null);

export const useVoiceRoom = () => {
  const context = useContext(VoiceRoomContext);
  if (!context) {
    throw new Error('useVoiceRoom must be used within VoiceRoomProvider');
  }
  return context;
};

// ============================================
// AUDIO ELEMENTS CONTAINER
// Pour jouer l'audio des participants distants
// ============================================

const audioElements = new Map();

const attachTrackToAudioElement = (track, participantId) => {
  // Créer un élément audio pour ce participant s'il n'existe pas
  let audioEl = audioElements.get(participantId);
  if (!audioEl) {
    audioEl = document.createElement('audio');
    audioEl.id = `audio-${participantId}`;
    audioEl.autoplay = true;
    audioEl.playsInline = true;
    document.body.appendChild(audioEl);
    audioElements.set(participantId, audioEl);
    console.log(`[VOICE] 🔊 Audio element créé pour ${participantId}`);
  }
  
  // Attacher la piste audio
  track.attach(audioEl);
  console.log(`[VOICE] 🎵 Track audio attaché pour ${participantId}`);
};

const detachAudioElement = (participantId) => {
  const audioEl = audioElements.get(participantId);
  if (audioEl) {
    audioEl.srcObject = null;
    audioEl.remove();
    audioElements.delete(participantId);
    console.log(`[VOICE] 🔇 Audio element supprimé pour ${participantId}`);
  }
};

// ============================================
// PROVIDER
// ============================================

export const VoiceRoomProvider = ({ children, userId, userName }) => {
  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  
  // Refs
  const roomRef = useRef(null);
  const localAudioTrackRef = useRef(null);

  // ============================================
  // UPDATE PARTICIPANTS
  // ============================================

  const updateParticipants = useCallback((room, activeSpeakers = []) => {
    const participantsList = [];

    // Local participant (moi)
    if (room.localParticipant) {
      const isMeSpeaking = activeSpeakers.some(s => s.sid === room.localParticipant.sid);
      participantsList.push({
        id: room.localParticipant.identity,
        name: room.localParticipant.name || 'Moi',
        isLocal: true,
        isMuted: !room.localParticipant.isMicrophoneEnabled,
        isSpeaking: isMeSpeaking
      });
    }

    // Remote participants
    room.remoteParticipants.forEach((participant) => {
      const speaking = activeSpeakers.some(s => s.sid === participant.sid);
      participantsList.push({
        id: participant.identity,
        name: participant.name || participant.identity,
        isLocal: false,
        isMuted: !participant.isMicrophoneEnabled,
        isSpeaking: speaking
      });
    });

    setParticipants(participantsList);
  }, []);

  // ============================================
  // HANDLE DISCONNECT
  // ============================================

  const handleDisconnect = useCallback(() => {
    // Nettoyer tous les éléments audio
    audioElements.forEach((_, participantId) => {
      detachAudioElement(participantId);
    });
    
    setIsConnected(false);
    setCurrentRoom(null);
    setParticipants([]);
    setIsMuted(false);
    setIsSpeaking(false);
    localAudioTrackRef.current = null;
  }, []);

  // ============================================
  // CONFIGURATION
  // ============================================

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/voice/config`);
      const data = await res.json();
      if (data.configured) {
        setServerUrl(data.serverUrl);
      } else {
        setError('Service vocal non configuré');
      }
    } catch (err) {
      console.error('Erreur config voice:', err);
      setError('Impossible de charger la configuration');
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // ============================================
  // JOIN ROOM
  // ============================================

  const joinRoom = useCallback(async (roomId, roomName) => {
    if (isConnecting || isConnected) {
      console.warn('Déjà connecté ou en cours de connexion');
      return;
    }

    if (!serverUrl) {
      toast.error('Service vocal non disponible');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // 1. Obtenir le token du backend
      const tokenRes = await fetch(`${API_URL}/api/voice/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId,
          userId: userId,
          userName: userName
        })
      });

      if (!tokenRes.ok) {
        const err = await tokenRes.json();
        throw new Error(err.detail || 'Erreur token');
      }

      const { token } = await tokenRes.json();

      // 2. Créer la room LiveKit
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // 3. Event listeners - Participants
      room.on(RoomEvent.ParticipantConnected, (participant) => {
        console.log('[VOICE] Participant connecté:', participant.identity);
        updateParticipants(room);
        toast.success(`${participant.name || participant.identity} a rejoint le salon`);
      });

      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('[VOICE] Participant déconnecté:', participant.identity);
        // Supprimer l'élément audio du participant qui part
        detachAudioElement(participant.identity);
        updateParticipants(room);
        toast.info(`${participant.name || participant.identity} a quitté le salon`);
      });

      // 🔊 CRUCIAL: Gérer les pistes audio entrantes
      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log(`[VOICE] 🎵 Track souscrit: ${track.kind} de ${participant.identity}`);
        if (track.kind === Track.Kind.Audio) {
          // Attacher l'audio pour l'écouter !
          attachTrackToAudioElement(track, participant.identity);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        console.log(`[VOICE] 🔇 Track désouscrit: ${track.kind} de ${participant.identity}`);
        if (track.kind === Track.Kind.Audio) {
          detachAudioElement(participant.identity);
        }
      });

      // Gérer quand un participant active/désactive son micro
      room.on(RoomEvent.TrackMuted, (publication, participant) => {
        console.log(`[VOICE] 🔇 ${participant.identity} a coupé son micro`);
        updateParticipants(room);
      });

      room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
        console.log(`[VOICE] 🎤 ${participant.identity} a activé son micro`);
        updateParticipants(room);
      });

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        const isMeSpeaking = speakers.some(s => s.identity === userId);
        setIsSpeaking(isMeSpeaking);
        updateParticipants(room, speakers);
      });

      room.on(RoomEvent.Disconnected, () => {
        console.log('[VOICE] Déconnecté du salon');
        handleDisconnect();
      });

      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        console.log('[VOICE] État connexion:', state);
        if (state === ConnectionState.Connected) {
          setIsConnected(true);
          setIsConnecting(false);
        }
      });

      // 4. Connecter à la room
      await room.connect(serverUrl, token);
      console.log('[VOICE] ✅ Connecté à LiveKit!');

      // 5. Publier l'audio local
      await room.localParticipant.setMicrophoneEnabled(true);
      localAudioTrackRef.current = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      console.log('[VOICE] 🎤 Micro activé et publié');

      // 6. Attacher les pistes audio des participants déjà présents
      room.remoteParticipants.forEach((participant) => {
        participant.audioTrackPublications.forEach((publication) => {
          if (publication.track && publication.isSubscribed) {
            attachTrackToAudioElement(publication.track, participant.identity);
          }
        });
      });

      // 7. Enregistrer sur le backend
      await fetch(`${API_URL}/api/voice/rooms/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, userId: userId, userName: userName })
      });

      // 8. Mettre à jour l'état
      roomRef.current = room;
      setCurrentRoom({ id: roomId, name: roomName });
      setIsConnected(true);
      setIsMuted(false);
      updateParticipants(room);

      toast.success(`🎙️ Connecté à "${roomName}" !`);

    } catch (err) {
      console.error('[VOICE] Erreur joinRoom:', err);
      setError(err.message);
      toast.error(`Erreur connexion : ${err.message}`);
      handleDisconnect();
    } finally {
      setIsConnecting(false);
    }
  }, [serverUrl, userId, userName, isConnecting, isConnected, updateParticipants, handleDisconnect]);

  // ============================================
  // LEAVE ROOM
  // ============================================

  const leaveRoom = useCallback(async () => {
    if (roomRef.current) {
      // Notifier le backend
      if (currentRoom) {
        try {
          await fetch(`${API_URL}/api/voice/rooms/${currentRoom.id}/leave?userId=${userId}`, {
            method: 'POST'
          });
        } catch (err) {
          console.error('Erreur leave:', err);
        }
      }

      // Déconnecter LiveKit
      await roomRef.current.disconnect();
      roomRef.current = null;
    }

    handleDisconnect();
    toast.info('Tu as quitté le salon');
  }, [currentRoom, userId, handleDisconnect]);

  // ============================================
  // TOGGLE MUTE
  // ============================================

  const toggleMute = useCallback(async () => {
    if (!roomRef.current) return;

    const newMuted = !isMuted;
    await roomRef.current.localParticipant.setMicrophoneEnabled(!newMuted);
    setIsMuted(newMuted);
    
    toast.info(newMuted ? '🔇 Micro coupé' : '🎤 Micro activé');
  }, [isMuted]);

  // ============================================
  // CLEANUP
  // ============================================

  useEffect(() => {
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
      // Nettoyer tous les éléments audio
      audioElements.forEach((_, participantId) => {
        detachAudioElement(participantId);
      });
    };
  }, []);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // State
    isConnected,
    isConnecting,
    currentRoom,
    participants,
    isMuted,
    isSpeaking,
    error,
    
    // Actions
    joinRoom,
    leaveRoom,
    toggleMute,
    
    // Meta
    serverUrl,
    userId,
    userName
  };

  return (
    <VoiceRoomContext.Provider value={value}>
      {children}
    </VoiceRoomContext.Provider>
  );
};

export default VoiceRoomProvider;
